import { Facebook, Instagram } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { CLINIC, WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

export const Footer = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const NAV_LINKS = [
    { id: "inicio", label: t("nav.inicio") },
    { id: "quem-somos", label: t("nav.quemSomos") },
    { id: "servicos", label: t("nav.servicos") },
    { id: "testemunhos", label: t("nav.testemunhos") },
    { id: "contacto", label: t("nav.contacto") },
  ];
  const FOOTER_SERVICES = t("footer.services");

  const scrollTo = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else if (attempts++ < 10) {
          setTimeout(tryScroll, 80);
        }
      };
      setTimeout(tryScroll, 80);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0F1A17] text-[#FEFFFE] pt-20 pb-10"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10 pb-16 border-b border-[#FEFFFE]/15">
          <div className="text-center md:text-left">
            <img
              src="https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/0fhdtt2f_logo%20para%20site.png"
              alt="Yin Motion — Saúde & Bem Estar"
              data-testid="footer-logo"
              className="h-24 md:h-28 w-auto object-contain mb-5 mx-auto md:mx-0 md:-ml-2"
            />
            <p className="text-[#FEFFFE] leading-relaxed max-w-xs mx-auto md:mx-0">
              {t("footer.tagline", { year: CLINIC.foundedYear })}
            </p>

            <div className="mt-5 flex items-center justify-center md:justify-start gap-4">
              <a
                href={CLINIC.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook Yin Motion"
                data-testid="footer-social-facebook"
                className="text-[#FEFFFE]/85 hover:text-[#EE8984] transition-colors duration-300"
              >
                <Facebook size={20} strokeWidth={1.5} />
              </a>
              <a
                href={CLINIC.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Yin Motion"
                data-testid="footer-social-instagram"
                className="text-[#FEFFFE]/85 hover:text-[#EE8984] transition-colors duration-300"
              >
                <Instagram size={20} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="text-center md:text-left">
            <div className="text-[13px] uppercase tracking-[0.32em] font-bold text-[#FEFFFE] mb-6">
              {t("footer.navHeading")}
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    data-testid={`footer-link-${l.id}`}
                    className="text-[#FEFFFE] hover:text-[#EE8984] transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center md:text-left">
            <div className="text-[13px] uppercase tracking-[0.32em] font-bold text-[#FEFFFE] mb-6">
              {t("footer.servicesHeading")}
            </div>
            <ul className="space-y-3">
              {FOOTER_SERVICES.map((s) => (
                <li key={s}>
                  <button
                    onClick={() => scrollTo("servicos")}
                    data-testid={`footer-service-${s}`}
                    className="text-[#FEFFFE] hover:text-[#EE8984] transition-colors"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center md:text-left">
            <div className="text-[13px] uppercase tracking-[0.32em] font-bold text-[#FEFFFE] mb-6">
              {t("footer.contactHeading")}
            </div>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${CLINIC.phoneRaw}`}
                  data-testid="footer-phone"
                  className="text-[#FEFFFE] hover:text-[#EE8984] transition-colors"
                >
                  {CLINIC.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CLINIC.email}`}
                  data-testid="footer-email"
                  className="text-[#FEFFFE] hover:text-[#EE8984] transition-colors"
                >
                  {CLINIC.email}
                </a>
              </li>
              <li className="text-[#FEFFFE]">{CLINIC.addressLine}</li>
              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="inline-block mt-2 border border-[#FEFFFE]/40 px-4 py-2 text-sm hover:bg-[#FEFFFE] hover:text-[#0F1A17] transition-colors"
                >
                  {t("footer.whatsappBtn")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row justify-between items-center sm:items-center gap-4 text-xs text-[#FEFFFE]/65 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-5">
            <span>
              © {new Date().getFullYear()} {t("footer.rights")}
            </span>
            <a
              href="https://www.livroreclamacoes.pt/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-livro-reclamacoes"
              className="underline underline-offset-4 decoration-[#FEFFFE]/30 hover:text-[#EE8984] hover:decoration-[#EE8984] transition-colors"
            >
              Livro de Reclamações
            </a>
          </div>
          <div className="flex flex-col items-center sm:items-end gap-[10px]">
            <div className="uppercase tracking-[0.22em]">
              {t("footer.location")}
            </div>
            <a
              href="https://b4idea.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-b4digital"
              className="text-[10.5px] tracking-[0.18em] text-[#FEFFFE]/55 hover:text-[#FEFFFE]/80 transition-opacity duration-300"
            >
              {t("footer.signature")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
